/**
 * Costruzione modello vista settimanale calendario e helper condivisi (no DOM modals).
 */

import { getState } from '../store.js';
import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT,
  UOC_LABELS,
  SLOT_CATEGORY_LABELS,
  TIME_SLOT_LABELS,
  ACTIVITY_TYPE_LABELS,
  TIME_SLOT_ORDER_CALENDAR,
  CATEGORY_TO_UOC,
  badgeClassForSlotCategory,
  getTemplateWeekdays,
} from '../types.js';
import { getOfficialSlotsForDates } from '../shiftGenerator.js';
import { computeWeeklyBalance } from '../workload.js';
import {
  getWeekDates,
  getMondayOfWeek,
  formatDateShort,
  formatDateIt,
  formatLocalISODate,
  ensurePlannerSlotName,
  getISOWeekString,
  getISOWeekNumber,
  addCalendarDays,
} from '../utils.js';
import { escapeHtmlAttr, escapeHtmlText } from './domSafe.js';

export {
  WEEKDAYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT,
  UOC_LABELS,
  SLOT_CATEGORY_LABELS,
  TIME_SLOT_LABELS,
  ACTIVITY_TYPE_LABELS,
  TIME_SLOT_ORDER_CALENDAR,
};

export const SHIFT_PLANNER_LOCKED_CALENDAR_MSG =
  'Questa settimana è archiviata o bloccata nel Piano turni: non è possibile modificare il calendario per date di quella settimana. Sblocca la settimana nel Piano turni per applicare questa modifica.';

export const LUN_VEN = new Set(['lun', 'mar', 'mer', 'gio', 'ven']);

export function shiftPlannerLockedWeeksTouchRange(dateFromIso, dateToIso) {
  const locked = getState().lockedWeeks || {};
  const df = (dateFromIso || '').slice(0, 10);
  const dt = (dateToIso || df || '').slice(0, 10);
  if (!df) return false;
  let mon = getMondayOfWeek(df);
  const endMon = getMondayOfWeek(dt);
  while (mon <= endMon) {
    if (locked[mon]) return true;
    mon = addCalendarDays(mon, 7);
  }
  return false;
}

export function formatTemplateWeekdaysShort(t) {
  const wds = getTemplateWeekdays(t);
  if (!wds.length) return '—';
  return wds.map((w) => WEEKDAY_SHORT[w] || w).join(', ');
}

export function formatTemplatePeriod(t) {
  if (!t.dateFrom && !t.dateTo) return '—';
  const a = t.dateFrom ? formatDateIt(t.dateFrom) : '…';
  const b = t.dateTo ? formatDateIt(t.dateTo) : '∞';
  return `${a} → ${b}`;
}

export function weekdayCheckboxesHtml(className, checkedSet) {
  return WEEKDAYS.map(
    (w) => `
    <label class="template-wd-label" style="font-size:0.8rem;margin-right:0.4rem;white-space:nowrap;">
      <input type="checkbox" class="${className}" value="${w}" ${checkedSet.has(w) ? 'checked' : ''}/> ${WEEKDAY_SHORT[w]}
    </label>
  `,
  ).join('');
}

export function rangesOverlap(aFrom, aTo, bFrom, bTo) {
  const min = '0000-01-01';
  const max = '9999-12-31';
  const a1 = (aFrom || '').slice(0, 10) || min;
  const a2 = (aTo || '').slice(0, 10) || max;
  const b1 = (bFrom || '').slice(0, 10) || min;
  const b2 = (bTo || '').slice(0, 10) || max;
  return a1 <= b2 && b1 <= a2;
}

export function collectConflictingTemplateNames(timeSlot, weekdays, dateFrom, dateTo, excludeId = null) {
  const st = getState();
  const targetDays = new Set(weekdays || []);
  if (!targetDays.size) return [];
  return (st.calendarTemplates || [])
    .filter((t) => {
      if (!t || t.id === excludeId) return false;
      if (t.timeSlot !== timeSlot) return false;
      const wds = getTemplateWeekdays(t);
      if (!wds.some((w) => targetDays.has(w))) return false;
      return rangesOverlap(t.dateFrom || null, t.dateTo || null, dateFrom || null, dateTo || null);
    })
    .map((t) => ensurePlannerSlotName(t.category, t.name, t.timeSlot, t.activityType || 'clinico'))
    .filter(Boolean);
}

export function requiredUocIdForCategory(category) {
  return CATEGORY_TO_UOC[category] || '';
}

export function applyCategoryToUocSelect(category, uocSelect) {
  if (!uocSelect) return '';
  const def = requiredUocIdForCategory(category);
  if (def && [...uocSelect.options].some((o) => o.value === def)) {
    uocSelect.value = def;
    return def;
  }
  return def;
}

/** Modello derivato per la tab preview + metadati navigazione/settimana. */
export function buildCalendarWeekPreviewModel(state) {
  const weekStart = getMondayOfWeek(state.calendarWeekStart || formatLocalISODate(new Date()));
  const weekDates = getWeekDates(weekStart);
  const weekSlots = getOfficialSlotsForDates(weekDates);
  const slotsByCell = {};
  weekSlots.forEach((s) => {
    const key = `${s.date}-${s.timeSlot}`;
    if (!slotsByCell[key]) slotsByCell[key] = [];
    slotsByCell[key].push(s);
  });
  const weekLabel = `${formatDateShort(weekDates[0])} – ${formatDateShort(weekDates[6])}`;
  const isoWeek = getISOWeekString(weekDates[0]);
  const currentYear = new Date(weekStart + 'T12:00:00').getFullYear();
  const currentWeekNum = getISOWeekNumber(weekStart);
  const balance = computeWeeklyBalance(weekStart);
  return {
    weekStart,
    weekDates,
    weekSlots,
    slotsByCell,
    weekLabel,
    isoWeek,
    currentYear,
    currentWeekNum,
    balance,
  };
}

/** Badge nella griglia settimanale; testo e attributi escapati dove serve */
export function weekCellBadgeHtml(s, canEdit) {
  const lbl = ensurePlannerSlotName(s.category, s.name, s.timeSlot, s.activityType || 'clinico');
  const cls = badgeClassForSlotCategory(s.category, s);
  const escNameAttr = escapeHtmlAttr(lbl);
  const titleTxt = canEdit ? 'Click sul badge per rimuovere' : '';
  const titleEsc = escapeHtmlAttr(titleTxt);
  return `<span class="badge badge-${cls} week-slot-badge" style="margin: 1px; cursor: pointer;" data-date="${escapeHtmlAttr(
    s.date,
  )}" data-timeslot="${escapeHtmlAttr(s.timeSlot)}" data-category="${escapeHtmlAttr(
    s.category,
  )}" data-uocid="${escapeHtmlAttr(s.uocId || '')}" data-name="${escNameAttr}" data-oneoff-mod-id="${escapeHtmlAttr(
    s._oneOffModId || '',
  )}" title="${titleEsc}">${escapeHtmlText(lbl)}</span>`;
}
